import { Moment } from 'moment';
import * as moment from 'moment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SettingsService } from '../shared/services/settings/settings.service';
import { PayGroup } from '../shared/models/pay-group.model';
import urlJoin from 'url-join';
import { Observable ,  of } from 'rxjs';
import { Injectable } from '@angular/core';
import { PayInfo, PayItemInfo } from './pay-info.model';
import { PaymentType, PayCustomerItemStatus, PayCustomerAction } from '../shared/enums/smarthub-enums';
import { PayInfoView } from '../components/pay-info/pay-info-view.model';
import { PayPortionActionInfo, PayPortionActionResult } from './pay-portion-action-info.model';
import { DisplayStaleDatedChecksInfo }
from '../components/stale-dated-checks-info/stale-dated-checks-info-table/stale-dated-checks-info-table.component';
import { DurationModel, StaleDatedCheckDurationType} from '../components/stale-dated-checks-info/stale-dated-check-view.model';
import { ROUTE_TOKEN_GATEWAY_URL } from '../shared/interceptors/gateway-url.interceptor';

@Injectable()
export class UIPaymentService {
  constructor(
    private http: HttpClient,
    private settings: SettingsService) { }
    private durationTypes = DurationModel.types;

  getPayInfo(
    view: PayInfoView,
    from: Date | Moment,
    to: Date | Moment,
    paySequences: number[],
    payGroups: PayGroup[] | string[],
    paymentTypes: PaymentType[],
    amount: number,
    employeeNumbers: string[],
    firstNames: string[],
    lastNames: string[],
    itemNumber: string,
    statuses: PayCustomerItemStatus[],
    pageIndex?: number,
    pageSize?: number):
    Observable<PayInfo[]> {

    if (!(paymentTypes && paymentTypes.length > 0)) {
      return of([]);
    }

    const params = this._getQueryParameters(
      view,
      from,
      to,
      paySequences,
      payGroups,
      paymentTypes,
      amount,
      employeeNumbers,
      firstNames,
      lastNames,
      itemNumber,
      statuses,
      pageIndex,
      pageSize
    );
    const uri = this._getUri('pays/customerinfos', params);

    return this.http.get<PayInfo[]>(uri);
  }

  performAction(action: PayCustomerAction, portions: PayPortionActionInfo[], email: string): Observable<PayPortionActionResult[]> {
    if (!portions || !portions.length) {
      return of([]);
    }

    const uri = this._getUri('pays/actions');

    return this.http.post<PayPortionActionResult[]>(uri, {
      action,
      portions,
      notifyToEmailAddress: email
    });
  }

  private _payItemHasAmount(payItem: PayItemInfo, amount: number) {
    return this._isAmountValid(amount) && payItem.amount === amount;
  }

  private _isAmountValid(amount: number): boolean {
    return !(isNaN(amount) || amount === null);
  }

  private _getUri(path: string, params: string = null) {
    let url = urlJoin(this._getBaseUrl(), `cui-payment/v1/tenants/${encodeURI(this.settings.tenantId)}`);
    url = urlJoin(url, path);
    if (params) {
      url += '?' + params;
    }
    return url;
  }

  private _getBaseUrl(): string {
    return ROUTE_TOKEN_GATEWAY_URL;
  }

  private _getQueryParametersForStaleDatedCheck(
    futureDays: number,
    pastDays: number,
    payGroups: PayGroup[] | string[],
    checkAmount: number,
    employeeNumbers: string[],
    firstNames: string[],
    lastNames: string[],
    checkNo: string,
    pageIndex?: number,
    pageSize?: number
  ): string {
    const mappedGroups = this._getGroupCodes(payGroups);
    let params = new HttpParams();

    params = this._appendArray(params, 'payGroups', mappedGroups);

    if (this._isAmountValid(checkAmount)) {
      params = params.append('checkAmount', checkAmount.toString());
    }
    params = this._appendArray(params, 'employeeNum', employeeNumbers);
    params = this._appendArray(params, 'firstName', firstNames);
    params = this._appendArray(params, 'lastName', lastNames);
    if (checkNo) {
      params = params.append('checkNumber', checkNo);
    }

    if (pageIndex !== null || pageSize !== null) {
      params = params.append('page', pageIndex.toString());
      params = params.append('per_page', pageSize.toString());
    }

    if (futureDays !== null && pastDays !== null) {
      params = params.append('futureDays', futureDays.toString());
      params = params.append('pastDays', pastDays.toString());
    }
    return params.toString();
  }

  private _getQueryParameters(
    view: PayInfoView,
    from: Date | Moment,
    to: Date | Moment,
    paySequences: number[],
    payGroups: PayGroup[] | string[],
    paymentTypes: PaymentType[],
    amount: number,
    employeeNumbers: string[],
    firstNames: string[],
    lastNames: string[],
    itemNumber: string,
    statuses: PayCustomerItemStatus[],
    pageIndex?: number,
    pageSize?: number): string {

    const mappedGroups = this._getGroupCodes(payGroups);
    paySequences = paySequences || [];
    paymentTypes = paymentTypes || [];

    let params = new HttpParams()
      .append('fromDate', moment(from).format('YYYY-MM-DD'))
      .append('toDate', moment(to).format('YYYY-MM-DD'));

    params = this._appendArray(params, 'payGroups', mappedGroups);
    params = this._appendArray(params, 'paySequences', paySequences);
    params = this._appendArray(params, 'paymentTypes', paymentTypes);

    if (this._isAmountValid(amount)) {
      params = params.append('amount', amount.toString());
    }
    params = this._appendArray(params, 'employeeNum', employeeNumbers);
    params = this._appendArray(params, 'firstName', firstNames);
    params = this._appendArray(params, 'lastName', lastNames);
    if (itemNumber) {
      params = params.append('itemNumber', itemNumber);
    }
    params = this._appendArray(params, 'status', statuses);

    if (pageIndex !== null || pageSize !== null) {
      params = params.append('page', pageIndex.toString());
      params = params.append('per_page', pageSize.toString());
    }

    params = params.append('viewType', view.toString());

    return params.toString();
  }

  private _appendArray(
    params: HttpParams, name: string, value: any[]): HttpParams {

    value = value || [];

    if (value.length > 0) {
      return params.append(name, value.join(','));
    }

    return params;
  }

  private _getGroupCodes(payGroups: PayGroup[] | string[]) {
    const mappedGroups = [];

    if (payGroups) {
      for (const p of payGroups) {
        const group = typeof p === 'string' ? p : (p as PayGroup).code;
        mappedGroups.push(group);
      }
    }

    return mappedGroups;
  }

  getStaleDatedChecksPayInfo(
    duration: StaleDatedCheckDurationType,
    payGroups: PayGroup[] | string[],
    checkAmount: number,
    employeeNumbers: string[],
    firstNames: string[],
    lastNames: string[],
    checkNo: string,
    pageIndex?: number,
    pageSize?: number
  ): Observable<DisplayStaleDatedChecksInfo[]> {

    const totalDuration = this.durationTypes.get(duration);

    const params = this._getQueryParametersForStaleDatedCheck(
      totalDuration.futureDays,
      totalDuration.pastDays,
      payGroups,
      checkAmount,
      employeeNumbers,
      firstNames,
      lastNames,
      checkNo,
      pageIndex,
      pageSize
    );
    const uri = this._getUri('pays/staleDatedChecksInfos', params.toString());
    return this.http.get<DisplayStaleDatedChecksInfo[]>(uri);
  }
}
